import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Users, Shield, Menu, Database, Activity, TrendingUp } from 'lucide-react'

interface DashboardStats {
  totalUsers: number
  totalRoles: number
  totalMenus: number
  totalCRUDs: number
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalRoles: 0,
    totalMenus: 0,
    totalCRUDs: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Simular carga de estadísticas
    setTimeout(() => {
      setStats({
        totalUsers: 25,
        totalRoles: 3,
        totalMenus: 12,
        totalCRUDs: 8
      })
      setLoading(false)
    }, 1000)
  }, [])

  const StatCard = ({ icon: Icon, title, value, color, trend }: any) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <div className="flex items-baseline">
            <p className="text-2xl font-semibold text-gray-900">
              {loading ? '...' : value}
            </p>
            {trend && (
              <span className="ml-2 text-sm font-medium text-green-600 flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                {trend}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">
          Bienvenido de vuelta, <span className="font-medium">{user?.firstName || user?.email}</span>
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Users}
          title="Usuarios Totales"
          value={stats.totalUsers}
          color="bg-blue-600"
          trend="+12%"
        />
        <StatCard
          icon={Shield}
          title="Roles Activos"
          value={stats.totalRoles}
          color="bg-green-600"
          trend="+3%"
        />
        <StatCard
          icon={Menu}
          title="Menús Configurados"
          value={stats.totalMenus}
          color="bg-purple-600"
          trend="+8%"
        />
        <StatCard
          icon={Database}
          title="CRUDs Generados"
          value={stats.totalCRUDs}
          color="bg-orange-600"
          trend="+15%"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Acciones Rápidas</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <button className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Users className="h-5 w-5 text-blue-600 mr-3" />
              <span className="text-sm font-medium text-gray-900">Gestionar Usuarios</span>
            </button>
            <button className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Shield className="h-5 w-5 text-green-600 mr-3" />
              <span className="text-sm font-medium text-gray-900">Configurar Roles</span>
            </button>
            <button className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Database className="h-5 w-5 text-orange-600 mr-3" />
              <span className="text-sm font-medium text-gray-900">Generar CRUD</span>
            </button>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Actividad Reciente
          </h3>
          <div className="space-y-3">
            {[
              { action: 'Usuario creado', detail: 'juan.perez@email.com', time: 'Hace 2 horas' },
              { action: 'Rol actualizado', detail: 'Editor - Permisos modificados', time: 'Hace 4 horas' },
              { action: 'CRUD generado', detail: 'Tabla: productos', time: 'Hace 1 día' },
              { action: 'Menú configurado', detail: 'Dashboard Analytics', time: 'Hace 2 días' }
            ].map((item, index) => (
              <div key={index} className="flex items-center py-2">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{item.action}</p>
                  <p className="text-sm text-gray-500">{item.detail}</p>
                </div>
                <span className="text-xs text-gray-400">{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
